import * as React from 'react';
import {
  AgencyConnectureRepository,
  AgencyDownlineRepository,
  AgencyRepository,
  UserConnectureRepository,
  WebAPI,
} from '../../MedicareProAPI';
import type { Agency } from '../../MedicareProAPI/Entities/Agency/Agency';
import type { AgencyConnecture } from '../../MedicareProAPI/Entities/Agency/AgencyConnecture';
import type { AgencyDownline } from '../../MedicareProAPI/Entities/Agency/AgencyDownline';
import type { UserConnecture } from '../../MedicareProAPI/Entities/User/UserConnecture';

interface AgencyConnectureSetupProps {
  userAccessToken: string;
  userId: number;
  agencyId: number;
  thisAgency: Agency;
  thisConnecture?: AgencyConnecture;
}

type CnxUI = { enabled: boolean; id?: number; username: string; pending?: boolean };

/**
 * Panel for setting up agency connecture — with paginated downlines
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-30
 * @lastUpdated 2025-10-30
 * @version 1.2.0 (adds pagination + fixes)
 */
export const AgencyConnectureSetup = ({
  userAccessToken,
  userId,
  agencyId,
  thisAgency,
  thisConnecture,
}: AgencyConnectureSetupProps): React.JSX.Element => {
  const exportFormRef = React.useRef<HTMLFormElement | null>(null);

  const [agency, setAgency] = React.useState<Agency>(thisAgency);
  const [connecture, setConnecture] = React.useState<AgencyConnecture | null>(thisConnecture);
  const [connectureData, setConnectureData] = React.useState<any>({});
  const [originalConnectureData, setOriginalConnectureData] = React.useState<any>({});
  const [addConnecture, setAddConnecture] = React.useState<boolean>(false);
  const [formIsDirty, setFormIsDirty] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const PAGE_SIZE = 50;
  const [downlines, setDownlines] = React.useState<AgencyDownline[]>([]);
  const [offset, setOffset] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);

  const lastFetchedOffsetRef = React.useRef<number>(-1);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [autoCnx, setAutoCnx] = React.useState<boolean>(agency.getAutoCnxAccess());
  const [cnxLocal, setCnxLocal] = React.useState<any>({});

  const [manage, setManage] = React.useState<boolean>(false);
  const [loadedOnce, setLoadedOnce] = React.useState(false);

  const agencyRepository = React.useMemo(() => {
    const r = new AgencyRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const agencyConnectureRepository = React.useMemo(() => {
    const r = new AgencyConnectureRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const agencyDownlineRepository = React.useMemo(() => {
    const r = new AgencyDownlineRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const userConnectureRepository = React.useMemo(() => {
    const r = new UserConnectureRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const webAPI = React.useMemo(() => {
    const r = new WebAPI();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const activeDownlineRows = React.useMemo(() => {
    const keep = new Set(['A', 'T']);
    return downlines.filter((d) => keep.has(d.getUser().getUserStatus()));
  }, [downlines]);

  const showEmpty = loadedOnce && !loading && activeDownlineRows.length === 0;
  /**
   * Reset pagination and load the first page when agency/user changes.
   *
   * @dependency agencyId, userId
   */
  React.useEffect(() => {
    setDownlines([]);
    setOffset(0);
    setHasMore(true);
    setLoading(true);
    setLoadingMore(false);
    lastFetchedOffsetRef.current = -1;

    void fetchDownlines(0, true);
  }, [agencyId, userId]);

  /**
   * Sync Connecture form state with the current agency Connecture instance.
   *
   * @dependency connecture
   */
  React.useEffect(() => {
    if (!connecture) {
      const blank = {
        url: '',
        urlRelay: '',
        apiKey: '',
        apiSecret: '',
        application: '',
        clientId: '',
        partnerId: '',
      };
      setConnectureData(blank);
      setOriginalConnectureData(blank);
    } else {
      handleMakeConnecture(connecture);
    }
  }, [connecture]);

  /**
   * Fetch a page of Connecture downlines (offset/limit) and merge into state.
   *
   * @param startOffset - Zero-based offset for the page to load
   * @param initial - If true, replaces the list; otherwise appends to it
   * @returns Promise<void>
   */
  const fetchDownlines = React.useCallback(
    async (startOffset: number, initial = false): Promise<void> => {
      // prevent duplicate fetch for the same offset
      if (loadingMore || startOffset === lastFetchedOffsetRef.current) return;

      setLoadingMore(true);
      lastFetchedOffsetRef.current = startOffset;

      try {
        const opts: any = {
          include: ['userConnecture', 'connecture'],
          limit: PAGE_SIZE,
          offset: startOffset,
          sortBy: 'userId',
        };
        const res: any = await agencyDownlineRepository.getAgencyDownlines(userId, agencyId, opts);

        const items: AgencyDownline[] = res.getItems();

        if (initial) setDownlines(items);
        else if (items.length) setDownlines((prev) => prev.concat(items));

        const total: number = res.getTotal();

        // if no items came back, stop trying further pages
        if (items.length === 0) {
          setHasMore(false);
          return;
        }

        const next = startOffset + items.length;
        setOffset(next);
        setHasMore(next < total);

        setLoadedOnce(true);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [agencyDownlineRepository, agencyId, userId, loadingMore]
  );

  /**
   * Load the next page of downlines if available.
   *
   * @returns void
   */
  const onLoadMore = React.useCallback((): void => {
    if (!hasMore || loadingMore) return;
    void fetchDownlines(offset, false);
  }, [hasMore, loadingMore, offset, fetchDownlines]);

  /**
   * Attach an infinite-scroll observer to the sentinel element.
   *
   * @dependency onLoadMore
   */
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries, obs) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          obs.unobserve(entry.target); // pause
          onLoadMore();
          // re-observe on the microtask queue so new content renders first
          queueMicrotask(() => el && obs.observe(el));
        }
      },
      { rootMargin: '1200px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore]);

  /**
   * Seed Connecture form state from the current agency Connecture instance.
   *
   * @param cnx - Agency Connecture entity
   * @returns void
   */
  const handleMakeConnecture = (cnx: AgencyConnecture): void => {
    const _connecture = {
      url: cnx.getUrl(),
      urlRelay: cnx.getUrlRelay(),
      apiKey: cnx.getApiKey(),
      apiSecret: cnx.getApiSecret(),
      application: cnx.getApplication(),
      clientId: cnx.getClientId(),
      partnerId: cnx.getPartnerId(),
    };
    setConnectureData(_connecture);
    setOriginalConnectureData(_connecture);
  };

  /**
   * Persist an agency flag update and mirror it locally.
   *
   * @param patch - Partial agency update (e.g., { autoCnxAccess: boolean })
   * @returns Promise<void>
   */
  const updateAgencyFlag = async (patch: any): Promise<void> => {
    if (!agency) return;
    await agencyRepository.updateAgency(userId, agencyId, patch);
    setAutoCnx(patch.autoCnxAccess);
  };

  /**
   * Create or update the agency's Connecture configuration (upsert) and sync UI state.
   *
   * @returns Promise<void>
   */
  const handleUpsertConnecture = async (): Promise<void> => {
    if (!connectureData) return;

    const errs = validateConnecture(connectureData);
    if (errs.length) {
      alert(errs.join('\n'));
      return;
    }

    setIsSaving(true);
    try {
      const id = typeof connecture?.getId === 'function' ? connecture.getId() : undefined;

      if (id) {
        // UPDATE
        await agencyConnectureRepository.updateAgencyConnecture(
          userId,
          agencyId,
          id,
          connectureData
        );
        setFormIsDirty(false);
        setIsSaving(false);
      } else {
        // ADD
        const created = await agencyConnectureRepository.addAgencyConnecture(
          userId,
          agencyId,
          connectureData
        );
        setFormIsDirty(false);
        setConnecture(created);
        setIsSaving(false);
        handleMakeConnecture(created);
      }
    } catch (e) {
      console.error(e);
      setIsSaving(false);
      alert('There was a problem saving Connecture. Please try again.');
    }
  };

  /**
   * Delete the current Connecture configuration (after confirmation) and reset form state.
   *
   * @returns Promise<void>
   */
  const handleDeleteConnecture = async (): Promise<void> => {
    if (!connecture) return;

    const isLast = true;
    const ok = window.confirm(
      isLast
        ? 'If you delete this, Connecture will be disabled for all brokers. Click "Ok" to proceed or "Cancel" to abort.'
        : 'Delete this Connecture instance?'
    );
    if (!ok) return;

    await agencyConnectureRepository.deleteAgencyConnecture(userId, agencyId, connecture.getId());

    setConnecture(null);
    const blank = {
      url: '',
      urlRelay: '',
      apiKey: '',
      apiSecret: '',
      application: '',
      clientId: '',
      partnerId: '',
    };
    setConnectureData(blank);
    setOriginalConnectureData(blank);
    setAddConnecture(false);

    activeDownlineRows.forEach((d) => {
      const u = d.getUser();
      const st = getRowState(d);

      if (!st.enabled) return;

      userConnectureRepository.deleteUserConnecture(u.getUserId(), st.id);
    });
  };

  /**
   * Optimistically toggle Connecture access for a downline user and sync to server.
   *
   * @param d - Agency downline row for the user
   * @param checked - Desired state (true = enable, false = disable)
   * @returns Promise<void>
   */
  const toggleConnectureForUser = async (d: AgencyDownline, checked: boolean): Promise<void> => {
    const u = d.getUser();
    const key = u.getUserId();

    if (checked) {
      if (!connecture) return; // need an agency connecture instance
      // optimistic enable; mark pending until we get an id
      setCnxLocal((m) => ({ ...m, [key]: { ...(m[key] || {}), enabled: true, pending: true } }));
      try {
        const resp = await userConnectureRepository.addUserConnecture(key, {
          agencyConnectureId: connecture.getId(),
        });
        const newId = resp.getId();
        setCnxLocal((m) => ({
          ...m,
          [key]: { ...(m[key] || {}), enabled: true, id: newId, pending: false },
        }));
      } catch (e) {
        console.error(e);
        // rollback on failure
        setCnxLocal((m) => ({
          ...m,
          [key]: { ...(m[key] || {}), enabled: false, id: undefined, pending: false },
        }));
      }
    } else {
      const server = getUserConnecture(d);
      const id = cnxLocal[key]?.id ?? server?.getId();
      if (!id) {
        // no id yet; just flip local off
        setCnxLocal((m) => ({ ...m, [key]: { ...(m[key] || {}), enabled: false, id: undefined } }));
        return;
      }
      try {
        await userConnectureRepository.deleteUserConnecture(key, id);
      } finally {
        setCnxLocal((m) => ({ ...m, [key]: { username: '', enabled: false, id: undefined } }));
      }
    }
  };

  /**
   * Update a user's Connecture username (optimistic UI).
   *
   * @param d - Agency downline row for the user
   * @param username - New username to persist
   * @returns Promise<void>
   */
  const saveConnectureUsername = async (d: AgencyDownline, username: string): Promise<void> => {
    const u = d.getUser();
    const key = u.getUserId();
    const state = getRowState(d);
    if (!state.id || !username) return;

    // optimistic update
    setCnxLocal((m) => ({ ...m, [key]: { ...(m[key] || {}), username } }));
    try {
      await userConnectureRepository.updateUserConnecture(key, state.id, { username });
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Revert Connecture form edits to the original snapshot.
   */
  const resetConnectureEdits = (): void => setConnectureData(originalConnectureData);

  /**
   * Shallow-compare two plain objects for value equality.
   *
   * @param a - First object to compare
   * @param b - Second object to compare
   * @returns True if keys and corresponding values are equal (shallow), else false
   */
  const shallowEqual = (a: any, b: any): boolean => {
    if (!a || !b) return a === b;
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if ((a[k] ?? '') !== (b[k] ?? '')) return false;
    }
    return true;
  };

  const isDirty = React.useMemo(
    () => !shallowEqual(connectureData, originalConnectureData),
    [connectureData, originalConnectureData]
  );

  /**
   * Get the first Connecture link for a downline user, if any.
   *
   * @param d - Agency downline row
   * @returns The user's UserConnecture entity or undefined
   */
  const getUserConnecture = (d: AgencyDownline): UserConnecture | undefined =>
    d.getUser().getUserConnecture().getItems()[0] as UserConnecture | undefined;

  /**
   * Compute Connecture row UI state by merging server entity with local edits.
   *
   * @param d - Agency downline row
   * @returns CnxUI with enabled flag, connecture id, and username
   */
  const getRowState = (d: AgencyDownline): CnxUI => {
    const u = d.getUser();
    const key = u.getUserId();
    const server = getUserConnecture(d);
    const local = cnxLocal[key];

    // base from server
    const base: CnxUI = {
      enabled: !!server,
      id: server?.getId(),
      username: server?.getUsername?.() ?? (server as any)?.username ?? '',
    };

    // overlay local edits if present
    return local ? { ...base, ...local } : base;
  };

  /**
   * Validate a Connecture payload and return user-facing error messages.
   *
   * @param p - Connecture form values
   * @returns Array of validation error strings (empty if valid)
   */
  const validateConnecture = (p: any): string[] => {
    const errors: string[] = [];
    const url = (p.url || '').toLowerCase();
    const urlRelay = (p.urlRelay || '').toLowerCase();

    if (!url.includes('saml')) errors.push('This is not a valid SAML URL.');
    if (!url.includes('destinationrx'))
      errors.push('This is not a valid enrollment URL (destinationrx).');
    if (!urlRelay.includes('destinationrx'))
      errors.push('This is not a valid relay state URL (destinationrx).');
    if (!p.partnerId) errors.push('A Partner ID is required.');
    if (!p.apiKey) errors.push('An API Key is required.');
    if (!p.apiSecret) errors.push('An API Secret is required.');

    return errors;
  };

  return (
    <>
      <div className="col-12  px-2  sm-resp" id="contact">
        <div className="card cardAddress">
          <div className="card-body mt-0" id="rxCards">
            <div className="row">
              <div className="col-12">
                <img
                  className="rounded ml-2 mt-3"
                  height="35px"
                  src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/connecture.png"
                />
              </div>
              <div className="col-12 mt-3 mb-5">
                <button
                  className={`btn border-orange text-orange btn-sm p-2 d-inline-block cp px-3 hover-shadow`}
                  style={{ borderRadius: '15px', background: 'none' }}
                  onClick={() => setManage(!manage)}
                >
                  <b>Manage</b>
                </button>
              </div>

              {manage && (
                <>
                  {/* LEFT COLUMN -------------------------------------------------- */}
                  <div className="col-lg-6 col-12 border-right">
                    <div className="mb-2">
                      <b>Account Data</b>
                    </div>
                    <div className="alert alert-sm alert-secondary py-1 mb-3">
                      <i className="fal fa-comment-exclamation mr-2"></i>
                      Save/edit your broker facing enrollment URL.
                    </div>

                    {connecture || addConnecture ? (
                      <div className="border rounded p-3">
                        <div className="form-group">
                          <label className="label">SAML Enrollment URL</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.url || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, url: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">Enrollment Relay State URL</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.urlRelay || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, urlRelay: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">Application Name</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.application || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, application: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">Client ID</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.clientId || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, clientId: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">Partner ID</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.partnerId || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, partnerId: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">API Key</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.apiKey || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, apiKey: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">API Secret</label>
                          <input
                            className="form-control form-control-sm"
                            value={connectureData?.apiSecret || ''}
                            onChange={(e) =>
                              setConnectureData({ ...connectureData, apiSecret: e.target.value })
                            }
                          />
                        </div>

                        <div>
                          {isDirty && (
                            <>
                              <button
                                className="btn btn-sm btn-success mr-2"
                                disabled={isSaving}
                                onClick={handleUpsertConnecture}
                              >
                                {!isSaving ? <>Save</> : <>Saving...</>}
                              </button>
                              <button
                                className="btn btn-sm btn-secondary mr-2"
                                onClick={resetConnectureEdits}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {connecture?.getId?.() && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={handleDeleteConnecture}
                            >
                              <i className="fal fa-trash" />
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary"
                        id="addCnxPlanYear"
                        onClick={() => setAddConnecture(true)}
                      >
                        Add Connecture Instance
                      </button>
                    )}
                  </div>

                  {/* RIGHT COLUMN ------------------------------------------------- */}
                  <div className="col-lg-6 col-12 ">
                    <div className="mb-2">
                      <b>Broker Access</b>
                    </div>
                    {connecture && (
                      <>
                        <div className="alert alert-sm alert-secondary py-1 mb-3">
                          <i className="fal fa-comment-exclamation mr-2"></i>
                          Enable/disable downline agent access to Connecture. If disabled, broker
                          will not see a Connecture enrollment option from a client profile page.
                        </div>

                        <div className="row align-items-center mb-3">
                          <div className="col-12">
                            <div className="d-flex align-items-center border-bottom pb-3">
                              <div className="mr-3">
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={autoCnx}
                                    onChange={(e) =>
                                      updateAgencyFlag({ autoCnxAccess: e.target.checked })
                                    }
                                  />
                                  <span className="slider round pt-1" />
                                </label>
                              </div>
                              <div className="mr-auto">
                                <strong>Enable/disable auto Connecture access</strong>
                              </div>
                              <button
                                className="badge badge-pill badge-primary border-0"
                                onClick={() => exportFormRef.current?.submit()}
                              >
                                Generate Broker List
                              </button>
                            </div>
                          </div>

                          {/* Export POST form */}
                          <div className="d-none">
                            <form
                              ref={exportFormRef}
                              method="post"
                              action={webAPI.exportAgencyConnectureBrokerListUrl(userId, agencyId)}
                            />
                          </div>
                        </div>

                        <div
                          className="border rounded"
                          style={{ maxHeight: '477px', overflowY: 'auto' }}
                        >
                          {activeDownlineRows.map((d) => {
                            const u = d.getUser();
                            const st = getRowState(d); // merged server+local
                            return (
                              <div
                                key={`cnx-${u.getUserId()}`}
                                className="row border-bottom pb-1 pt-1 mx-0"
                              >
                                <div className="col-12 font-weight-bold">
                                  {u.getUserFirstname()} {u.getUserLastname()}
                                </div>

                                <div className="col-3 p-2 ml-1">
                                  <label className="switch">
                                    <input
                                      type="checkbox"
                                      checked={st.enabled}
                                      onChange={(e) => toggleConnectureForUser(d, e.target.checked)}
                                    />
                                    <span className="slider round pt-1" />
                                  </label>
                                  {st.pending && (
                                    <span className="ml-2 small text-muted">
                                      <i className="fal fa-spinner fa-spin" /> linking…
                                    </span>
                                  )}
                                </div>

                                <div className="col-6 form-inline">
                                  <input
                                    disabled={!st.enabled || st.pending || !st.id} // disable until we have an id
                                    placeholder="Connecture Username"
                                    className="form-control form-control-sm w-100"
                                    value={st.username} // controlled
                                    onChange={(e) =>
                                      setCnxLocal((m) => ({
                                        ...m,
                                        [u.getUserId()]: {
                                          ...(m[u.getUserId()] || {}),
                                          username: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </div>

                                <div className="col-2">
                                  <button
                                    className="btn btn-sm btn-success d-inline-block"
                                    disabled={!st.enabled || st.pending || !st.id || !st.username}
                                    onClick={() => saveConnectureUsername(d, st.username)}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            );
                          })}

                          {showEmpty && <div className="p-3 text-muted">No brokers found.</div>}

                          {loadingMore && (
                            <div className="p-2 text-center">
                              <i className="fal fa-spinner fa-spin" /> Loading…
                            </div>
                          )}

                          {!loadingMore && hasMore && (
                            <div className="p-2 text-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={onLoadMore}
                              >
                                Load more
                              </button>
                            </div>
                          )}
                          <div ref={sentinelRef} />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
